/**
 * Category Counts Card — displays per-subset category counts as Doughnut or Bar charts.
 * Mutually exclusive subsets use a Doughnut; non-exclusive subsets use a horizontal Bar.
 */
import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';
import { CardComponentProps } from '@/lib/cards';
import { getDashboardCounts, DashboardSubset } from '@/api/dashboard';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

const CHART_COLOURS = [
  '#2e7032', '#2b7da8', '#f5c84a', '#e87830',
  '#6c757d', '#9c27b0', '#00bcd4', '#e91e63',
];

function SubsetChart({ subset }: { subset: DashboardSubset }) {
  const sorted = [...subset.categories].sort((a, b) => b.count - a.count);
  const labels = sorted.map((c) => c.categoryName);
  const counts = sorted.map((c) => c.count);
  const colours = sorted.map((c, i) => c.categoryColourHex ?? CHART_COLOURS[i % CHART_COLOURS.length]!);

  if (subset.isMutuallyExclusive) {
    return (
      <div style={{ position: 'relative', maxHeight: 280 }}>
        <Doughnut
          data={{ labels, datasets: [{ data: counts, backgroundColor: colours, borderWidth: 1 }] }}
          options={{
            responsive: true,
            maintainAspectRatio: true,
            plugins: { legend: { position: 'bottom', labels: { boxWidth: 12, padding: 16 } } },
            cutout: '60%',
          }}
        />
      </div>
    );
  }

  return (
    <Bar
      data={{ labels, datasets: [{ data: counts, backgroundColor: colours, borderWidth: 1 }] }}
      options={{
        indexAxis: 'y' as const,
        plugins: { legend: { display: false } },
        scales: {
          x: { beginAtZero: true, ticks: { precision: 0 } },
          y: { ticks: { font: { size: 12 } } },
        },
        maintainAspectRatio: true,
      }}
    />
  );
}

export const CategoryCountsCard: React.FC<CardComponentProps> = ({
  card: _card,
  userContext: _userContext,
  onError,
}) => {
  const [subsets, setSubsets] = useState<DashboardSubset[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        const response = await getDashboardCounts();
        setSubsets(response.subsets);
      } catch (err) {
        onError?.(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setLoading(false);
      }
    }

    void loadData();
  }, [onError]);

  if (loading) {
    return <div style={{ padding: '1rem', color: '#666' }}>Loading categories...</div>;
  }

  if (subsets.length === 0) {
    return (
      <div style={{ padding: '1rem', color: '#888', textAlign: 'center' }}>
        No categorisation data yet.
      </div>
    );
  }

  return (
    <div style={{ padding: '1rem' }}>
      {subsets.map((subset) => (
        <div key={subset.subsetId} style={{ marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{subset.subsetName}</span>
            <span style={{ fontSize: '0.8rem', color: '#888' }}>
              {subset.totalConversations} conversation{subset.totalConversations !== 1 ? 's' : ''}
            </span>
          </div>
          {subset.totalConversations === 0 ? (
            <p style={{ fontSize: '0.85rem', color: '#888' }}>No conversations in {subset.subsetName} yet.</p>
          ) : (
            <SubsetChart subset={subset} />
          )}
        </div>
      ))}
    </div>
  );
};

CategoryCountsCard.displayName = 'CategoryCountsCard';

export default CategoryCountsCard;
